import { Client, type Room } from '@colyseus/sdk';
import { NORMALIZED_WAVE_TABLE, type WaveComponent } from '../../shared/waveField';

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

export interface WaveEntryLike {
  dirX: number;
  dirZ: number;
  wavelength: number;
  amplitude: number;
  steepness: number;
  speed: number;
}

export interface SailLike {
  level: number;
  angle: number;
}

export interface CannonballLike {
  id: number;
  x: number;
  y: number;
  z: number;
}

export interface CannonStatusLike {
  side: string;
  cooldown: number;
  loaded: boolean;
}

/** Server-authoritative storm weather snapshot (mirrors `WeatherStateLike`). */
export interface WeatherLike {
  /** 'calm' | 'building' | 'storm' | 'easing'. */
  phase: string;
  storm: number;
  rain: number;
  cloud: number;
  fog: number;
  gust: number;
  lightning: boolean;
  /** Effective Gerstner amplitude the authoritative buoyancy is sampling. */
  seaScale: number;
}

/** What a crew member is doing this tick (mirrors `CrewTask` on the server). */
export type CrewTaskLike = 'idle' | 'trimming' | 'loading' | 'repairing';

export interface CrewMemberLike {
  id: string;
  role: string;
  x: number;
  z: number;
  station: number;
  /** What this member is doing this tick. */
  task: CrewTaskLike;
}

export interface StationLike {
  type: string;
  occupiedBy: string;
  sailIndex: number;
}

export interface ShipLike {
  x: number;
  y: number;
  z: number;
  rotationY: number;
  pitch: number;
  roll: number;
  vx: number;
  vy: number;
  vz: number;
  speed: number;
  health: number;
  rudder: number;
  sailArea: number;
  /** True while the shared anchor is down (player ship only). */
  anchored: boolean;
  /** Anchor drop point (world x, z). */
  anchorX: number;
  anchorZ: number;
  sails: ArrayLike<SailLike> & Iterable<SailLike>;
  stations: ArrayLike<StationLike> & Iterable<StationLike>;
}

export interface PlayerLike {
  id: string;
  name: string;
  station: string;
  deckX: number;
  deckZ: number;
  /** True while this player has stepped off the hull and walks in world space. */
  ashore: boolean;
  /** Authoritative world walk position (metres), valid while `ashore`. */
  worldX: number;
  worldZ: number;
  /** Authoritative world facing (radians) while ashore. */
  yaw: number;
}

export interface NetState {
  serverTime: number;
  gameState: string;
  windDirection: number;
  windStrength: number;
  waveTable: ArrayLike<WaveEntryLike> & Iterable<WaveEntryLike>;
  ship: ShipLike;
  /** The single synchronized AI enemy vessel, replacing the buoy collection. */
  enemyShip: ShipLike;
  players: Map<string, PlayerLike>;
  structures: Map<string, unknown>;
  cannonballs: ArrayLike<CannonballLike> & Iterable<CannonballLike>;
  cannonStatus: ArrayLike<CannonStatusLike> & Iterable<CannonStatusLike>;
  crew: ArrayLike<CrewMemberLike> & Iterable<CrewMemberLike>;
  /** Server-authoritative storm weather snapshot. */
  weather: WeatherLike;
  /** True while the NPC repair hand is actively restoring hull this tick. */
  repairing: boolean;
}

// The client and the authoritative room always share one origin: in dev the
// `colyseus/vite` plugin hosts the room on Vite's own server (5188), and the
// production build is the single-port deploy (`server.listen(2567)` serving
// `dist/client`). `vite preview` is client-only, so callers there must pass
// `VITE_SERVER_URL` explicitly.
const DEFAULT_URL = typeof location !== 'undefined'
  ? `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}`
  : 'ws://127.0.0.1:2567';

function resolveUrl(): string {
  const env = import.meta.env as Record<string, string | undefined>;
  return env.VITE_SERVER_URL ?? DEFAULT_URL;
}

/**
 * The only room name the server defines (`server/src/index.ts`). Clients cannot
 * invent a room name: matchmaking answers `code 520 "provided room name not
 * defined"` for anything else. Isolation is per room *instance* instead —
 * `?fresh=1` calls `create`, which always spins up a new room under this name.
 */
const ROOM_NAME = 'game_room';

/**
 * Client connection to the authoritative `game_room`.
 *
 * Connects on load, applies state patches, surfaces connection status and
 * retries with exponential backoff. Nothing here throws into the render loop:
 * a missing server simply leaves status `disconnected` while the scene keeps
 * rendering.
 */
export class ColyseusClient {
  readonly url: string;
  status: ConnectionStatus = 'connecting';
  room: Room | null = null;
  roomId = '';
  state: NetState | null = null;
  waveTable: WaveComponent[] = NORMALIZED_WAVE_TABLE.map((component) => ({ ...component }));
  sessionId = '';

  onPatch: ((state: NetState) => void) | null = null;
  onStatus: ((status: ConnectionStatus) => void) | null = null;

  private readonly client: Client;
  private retries = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;

  constructor(url: string = resolveUrl()) {
    this.url = url;
    this.client = new Client(url);
  }

  connect(): void {
    if (this.disposed) return;
    this.setStatus(this.room ? 'connected' : 'connecting');

    // Room selection: `?room=<id>` joins a specific room, `?fresh=1` creates a
    // new one, otherwise Colyseus matchmaking joins or creates the default room.
    const params = new URLSearchParams(
      typeof location !== 'undefined' ? location.search : '',
    );
    const requestedRoom = params.get('room');
    const join = requestedRoom
      ? this.client.joinById(requestedRoom)
      : params.has('fresh')
        ? this.client.create(ROOM_NAME)
        : this.client.joinOrCreate(ROOM_NAME);

    join
      .then((room) => {
        if (this.disposed) {
          void room.leave();
          return;
        }
        this.room = room;
        this.roomId = room.roomId;
        this.sessionId = room.sessionId;
        this.retries = 0;
        room.onStateChange((state) => this.receiveState(state as unknown as NetState));
        room.onError(() => this.handleDisconnect('error'));
        room.onLeave(() => this.handleDisconnect('disconnected'));
        this.setStatus('connected');
      })
      .catch(() => this.handleDisconnect('error'));
  }

  send(type: string, message?: unknown): void {
    this.room?.send(type, message);
  }

  dispose(): void {
    this.disposed = true;
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    const room = this.room;
    this.room = null;
    this.state = null;
    if (room) void room.leave();
  }

  private receiveState(state: NetState): void {
    this.state = state;
    this.syncWaveTable(state);
    this.onPatch?.(state);
  }

  private syncWaveTable(state: NetState): void {
    const incoming = state.waveTable;
    if (!incoming || incoming.length === 0) return;
    const next: WaveComponent[] = [];
    for (const entry of incoming) {
      next.push({
        dirX: entry.dirX,
        dirZ: entry.dirZ,
        wavelength: entry.wavelength,
        amplitude: entry.amplitude,
        steepness: entry.steepness,
        speed: entry.speed,
      });
    }
    if (next.length !== this.waveTable.length || next.some((entry, i) => entry.amplitude !== this.waveTable[i]?.amplitude)) {
      this.waveTable = next;
    }
  }

  private handleDisconnect(status: ConnectionStatus): void {
    if (this.disposed) return;
    this.room = null;
    this.roomId = '';
    this.state = null;
    this.setStatus(status);
    const delay = Math.min(8000, 750 * 2 ** this.retries);
    this.retries = Math.min(this.retries + 1, 4);
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }

  private setStatus(status: ConnectionStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.onStatus?.(status);
  }
}
